import { db, type LiftXDatabase } from '../db';
import type { CatalogueExercise, CatalogueProvenance, ExerciseRecord } from '../types';

/**
 * Seed the bundled catalogue into IndexedDB. Phase 0 §7.3.
 *
 * Three properties this must have, each of which is a real failure mode rather
 * than a nicety:
 *
 *   1. **Idempotent.** Running twice leaves 1,500 rows, not 3,000. The app
 *      calls this on every launch to heal a partial seed, so "already done" is
 *      the common case, not the exception.
 *   2. **Resumable.** A seed interrupted at row 900 — closed tab, killed app,
 *      dead battery — resumes from 900 on the next launch. Written in chunks
 *      so an interruption leaves committed rows behind rather than nothing.
 *   3. **Never destroys user state.** Favourites, notes, overrides and archive
 *      flags on catalogue rows are the user's, not the catalogue's. A re-seed
 *      refreshes catalogue-owned fields only, and skips any row the user has
 *      explicitly overridden.
 *
 * Media is deliberately NOT written here. The 1,500 GIFs live in Cache Storage
 * addressed by URL (§8.2) and are cached on first view by the service worker —
 * writing 138.8 MB of blobs into IndexedDB on first launch would be hostile and
 * would duplicate what the browser already does well.
 */

export const CATALOGUE_URL = '/catalogue/exercises.json';
export const PROVENANCE_URL = '/catalogue/provenance.json';

/** Where a catalogue GIF is served from. The service worker caches it on first view. */
export function catalogueMediaUrl(mediaFile: string): string {
  return `/catalogue/media/${mediaFile}`;
}

/** Rows are committed in chunks so an interrupted seed leaves progress behind. */
const CHUNK_SIZE = 250;

export interface SeedProgress {
  written: number;
  total: number;
}

export interface SeedResult {
  /** Rows inserted by this run. Zero on an already-seeded database. */
  inserted: number;
  /** Existing catalogue rows refreshed with new catalogue-owned values. */
  refreshed: number;
  /** Rows left untouched because the user had overridden them. */
  preserved: number;
  total: number;
}

/**
 * Convert a catalogue record into a database row.
 *
 * `source: 'exercisedb'` comes from `@liftx/core`'s `EXERCISE_SOURCES` — see
 * the note in `types.ts` about Phase 0 §7.1's differing wording.
 */
function toRecord(entry: CatalogueExercise, now: number): ExerciseRecord {
  return {
    id: entry.id,
    source: 'exercisedb',
    name: entry.name,
    nameSearch: entry.nameSearch,

    // Absent from this dataset (Phase 0 §7.5). Present for user-created
    // exercises and manual overrides in Phase 4.
    description: null,
    instructions: entry.instructions,
    tips: [],
    category: null,
    difficulty: null,
    notes: null,
    tags: [],

    modality: entry.modality,
    loadType: entry.loadType,
    bodyParts: entry.bodyParts,
    targetMuscles: entry.targetMuscles,
    secondaryMuscles: entry.secondaryMuscles,
    equipment: entry.equipment,

    hasManualOverride: false,
    isFavourite: false,
    isArchived: false,
    defaultRestSeconds: null,
    defaultIncrementG: null,
    derivedFromExerciseId: null,
    catalogueRecordId: entry.id,
    mediaIds: [],
    thumbnailMediaId: null,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Merge a catalogue record onto an existing row, preserving everything the
 * user owns.
 *
 * The split matters: catalogue-owned fields (name, taxonomy, instructions) may
 * be refreshed when the dataset is rebuilt; user-owned fields (favourite,
 * archived, notes, media, overrides) must survive untouched. Getting this
 * backwards would silently discard a user's library the first time the
 * catalogue was updated.
 */
/**
 * Exported so Phase 4B's "Revert to catalogue version" can re-run exactly this
 * logic for one row, rather than a second implementation of the same merge
 * growing somewhere else. Revert additionally clears `hasManualOverride`
 * after calling this — merging alone preserves it, correctly, for the normal
 * re-seed path that must never touch an overridden row's content.
 */
export function mergeOntoExisting(
  existing: ExerciseRecord,
  entry: CatalogueExercise,
  now: number,
): ExerciseRecord {
  return {
    ...existing,
    name: entry.name,
    nameSearch: entry.nameSearch,
    instructions: entry.instructions,
    modality: entry.modality,
    loadType: entry.loadType,
    bodyParts: entry.bodyParts,
    targetMuscles: entry.targetMuscles,
    secondaryMuscles: entry.secondaryMuscles,
    equipment: entry.equipment,
    catalogueRecordId: entry.id,
    updatedAt: now,
    // Everything else — isFavourite, isArchived, notes, tags, description,
    // tips, defaultRestSeconds, defaultIncrementG, mediaIds, thumbnailMediaId,
    // hasManualOverride, createdAt — is inherited from `existing` by the
    // spread above and is deliberately not listed here.
  };
}

/** Load the bundled catalogue. Same-origin and precached, so this works offline. */
export async function loadCatalogue(fetchImpl: typeof fetch = fetch): Promise<CatalogueExercise[]> {
  const response = await fetchImpl(CATALOGUE_URL);
  if (!response.ok) throw new Error(`Catalogue unavailable (HTTP ${response.status})`);
  return (await response.json()) as CatalogueExercise[];
}

export async function loadProvenance(
  fetchImpl: typeof fetch = fetch,
): Promise<CatalogueProvenance | null> {
  try {
    const response = await fetchImpl(PROVENANCE_URL);
    if (!response.ok) return null;
    return (await response.json()) as CatalogueProvenance;
  } catch {
    return null;
  }
}

/**
 * Write the catalogue into the database.
 *
 * Safe to call on every launch: an already-seeded database costs one count and
 * one keys read, and inserts nothing.
 */
export async function seedCatalogue(
  entries: CatalogueExercise[],
  options: { database?: LiftXDatabase; onProgress?: (progress: SeedProgress) => void } = {},
): Promise<SeedResult> {
  const database = options.database ?? db;
  const now = Date.now();

  const existingRows = await database.exercises.where('source').equals('exercisedb').toArray();
  const existingById = new Map(existingRows.map((row) => [row.id, row]));

  let inserted = 0;
  let refreshed = 0;
  let preserved = 0;

  for (let offset = 0; offset < entries.length; offset += CHUNK_SIZE) {
    const chunk = entries.slice(offset, offset + CHUNK_SIZE);
    const toWrite: ExerciseRecord[] = [];

    for (const entry of chunk) {
      const existing = existingById.get(entry.id);

      if (existing === undefined) {
        toWrite.push(toRecord(entry, now));
        inserted += 1;
        continue;
      }

      // The user has edited this row. The catalogue does not get to overwrite
      // it — not even the name, which is precisely what they may have changed.
      if (existing.hasManualOverride) {
        preserved += 1;
        continue;
      }

      toWrite.push(mergeOntoExisting(existing, entry, now));
      refreshed += 1;
    }

    // One transaction per chunk, not one for the whole seed: a single
    // transaction spanning 1,500 rows would roll all of them back on an
    // interruption, which is the opposite of resumable.
    if (toWrite.length > 0) {
      await database.transaction('rw', database.exercises, async () => {
        await database.exercises.bulkPut(toWrite);
      });
    }

    options.onProgress?.({ written: Math.min(offset + CHUNK_SIZE, entries.length), total: entries.length });
  }

  return { inserted, refreshed, preserved, total: entries.length };
}

/**
 * Seed only if needed, fetching the catalogue itself.
 *
 * Returns `null` when the catalogue is already complete, so a caller can tell
 * "nothing to do" from "seeded 1,500 rows" without comparing counts.
 */
export async function seedCatalogueIfNeeded(
  options: {
    database?: LiftXDatabase;
    fetchImpl?: typeof fetch;
    onProgress?: (progress: SeedProgress) => void;
  } = {},
): Promise<SeedResult | null> {
  const database = options.database ?? db;
  const entries = await loadCatalogue(options.fetchImpl ?? fetch);

  const existing = await database.exercises.where('source').equals('exercisedb').count();
  if (existing >= entries.length) return null;

  // Spread rather than passing `onProgress: undefined` — the project runs with
  // `exactOptionalPropertyTypes`, where an explicit undefined is not the same
  // as an absent property.
  return await seedCatalogue(entries, {
    database,
    ...(options.onProgress === undefined ? {} : { onProgress: options.onProgress }),
  });
}
